﻿package com.mgacreative.hotelru.feature.reports.ui.advanced

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import com.mgacreative.hotelru.core.tenant.TenantSession
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.*

data class DateRange(val start: LocalDate, val end: LocalDate) {
    val days: Int get() = start.until(end, DateTimeUnit.DAY).coerceAtLeast(1)
}

data class HotelMetrics(
    val occupancyRate: Double = 0.0,
    val adr: Double = 0.0,
    val revPar: Double = 0.0,
    val totalIncome: Double = 0.0,
    val totalExpense: Double = 0.0,
    val totalTax: Double = 0.0,
    val cancelRate: Double = 0.0,
    val cancelCount: Int = 0
)

class ReportsViewModel(
    private val supabaseClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : ViewModel() {

    private val _dateRange = MutableStateFlow(
        DateRange(
            Clock.System.todayIn(TimeZone.currentSystemDefault()).minus(30, DateTimeUnit.DAY),
            Clock.System.todayIn(TimeZone.currentSystemDefault())
        )
    )
    val dateRange: StateFlow<DateRange> = _dateRange.asStateFlow()

    private val _metrics = MutableStateFlow(HotelMetrics())
    val metrics: StateFlow<HotelMetrics> = _metrics.asStateFlow()

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    init {
        // Initial load
        calculateMetrics(_dateRange.value)
    }

    fun updateDateRange(start: LocalDate, end: LocalDate) {
        val newRange = DateRange(start, end)
        _dateRange.value = newRange
        // Acceptance Criteria: Rapor tarih aralığı değiştiğinde tüm grafikler yeniden hesaplanıyor
        calculateMetrics(newRange)
    }

    private fun calculateMetrics(range: DateRange) {
        viewModelScope.launch {
            _isLoading.value = true
            
            // Mock fetching data from DB bounded by range.start and range.end
            // In a real app, you would query Supabase payments, reservations, expenses tables.
            
            val totalRoomsInHotel = 0 // Gerçek veritabanından alınacak
            val totalAvailableRoomNights = 0
            
            // Veritabanı entegrasyonu tamamlanana kadar veriler 0 olarak gösteriliyor.
            val soldRoomNights = 0
            val totalRoomRevenue = 0.0
            
            val totalOtherIncome = 0.0
            val totalIncome = 0.0
            val totalExpense = 0.0
            val totalTax = 0.0
            
            val totalReservations = 0
            val cancelCount = 0
            val cancelRate = 0.0

            // Acceptance Criteria: ADR ve RevPAR hesaplamaları standart otelcilik formülleri
            // ADR = Toplam Oda Geliri / Satılan Oda Gecesi
            val adr = if (soldRoomNights > 0) totalRoomRevenue / soldRoomNights else 0.0
            
            // RevPAR = Toplam Oda Geliri / Mevcut Oda Gecesi
            val revPar = if (totalAvailableRoomNights > 0) totalRoomRevenue / totalAvailableRoomNights else 0.0
            
            // Occupancy = Satılan Oda Gecesi / Mevcut Oda Gecesi
            val occupancy = if (totalAvailableRoomNights > 0) (soldRoomNights.toDouble() / totalAvailableRoomNights) * 100 else 0.0

            _metrics.value = HotelMetrics(
                occupancyRate = occupancy,
                adr = adr,
                revPar = revPar,
                totalIncome = totalIncome,
                totalExpense = totalExpense,
                totalTax = totalTax,
                cancelRate = cancelRate,
                cancelCount = cancelCount
            )
            
            _isLoading.value = false
        }
    }

    fun exportReportAsPDF() {
        // Logic to generate and share/download PDF
    }

    fun exportReportAsCSV() {
        // Logic to generate CSV and share/download
    }
}


